// ─── Exportación a docx · Plan de Prevención y Control de la CAI ──────────
//
// Genera el documento Word del PPCAI (criterio SyB 2 · Manual BREEAM ES
// NC 2015 ED.02) a partir del plan almacenado en cc_planes_iniciales.
// Reutiliza los estilos oficiales BREEAM ya usados en informes técnicos
// del resto de la app (Frutiger LT 45 Light, headings Calvert MT verde
// #3D6764) para mantener coherencia visual.

import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType,
} from 'docx'
import { saveAs } from 'file-saver'

// Textos de referencia del manual (para reconstruir bullets y tabla)
// Se pasan como parámetros para no duplicar constantes con el editor.

const FONT_BODY = 'Frutiger LT 45 Light'
const COLOR_BREEAM = '3D6764'
const COLOR_ACCENT = '6CB43F'
const border = { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' }
const borders = { top: border, bottom: border, left: border, right: border }

// ─── Helpers de párrafo ───────────────────────────────────────────────────
function p(text, opts = {}) {
  return new Paragraph({
    children: [new TextRun({ text, font: opts.font || FONT_BODY, size: opts.size || 18, bold: opts.bold, italics: opts.italics, color: opts.color })],
    spacing: { before: opts.before ?? 60, after: opts.after ?? 120 },
    alignment: opts.alignment,
    indent: opts.indent,
  })
}

function h1(text) {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_1 })
}

function h2(text) {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_2 })
}

function subhead(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: 22, color: COLOR_ACCENT, font: FONT_BODY })],
    spacing: { before: 240, after: 100 },
  })
}

function bullet(text, indentLeft = 360) {
  return new Paragraph({
    children: [new TextRun({ text: `• ${text}`, font: FONT_BODY, size: 18 })],
    spacing: { after: 60 },
    indent: { left: indentLeft },
  })
}

function keyValueRow(label, value) {
  return new TableRow({
    children: [
      new TableCell({
        borders, width: { size: 3200, type: WidthType.DXA },
        shading: { fill: 'F5F0EA', type: ShadingType.CLEAR },
        margins: { top: 80, bottom: 80, left: 140, right: 140 },
        children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 18, font: FONT_BODY })] })],
      }),
      new TableCell({
        borders, width: { size: 6160, type: WidthType.DXA },
        margins: { top: 80, bottom: 80, left: 140, right: 140 },
        children: [new Paragraph({ children: [new TextRun({ text: value || '—', size: 18, font: FONT_BODY })] })],
      }),
    ],
  })
}

const FRECUENCIAS = {
  diaria: 'Diaria', semanal: 'Semanal', quincenal: 'Quincenal',
  mensual: 'Mensual', por_hito: 'Por hito constructivo', '': 'Sin definir',
}

// ═══════════════════════════════════════════════════════════════════════
//   EXPORT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════
export async function exportPpcaiToWord({
  plan,           // registro cc_planes_iniciales
  projectName,    // nombre del proyecto
  contratistaPrincipal,  // { nombre, rol } o null
  ELEMENTOS_MANUAL,      // PPCAI_ELEMENTOS_MANUAL importado del componente
  CATEGORIAS_PRODUCTOS,  // PPCAI_CATEGORIAS_PRODUCTOS
  SMACNA_APARTADOS,      // PPCAI_SMACNA_APARTADOS
}) {
  const cj = plan.contenido_json || {}
  const ca = cj.criterios_adicionales || {}
  const pc = cj.post_construccion || {}
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })

  const sections = []

  // ─── Portada ────────────────────────────────────────────────────────────
  sections.push(
    new Paragraph({
      children: [new TextRun({ text: 'PLAN DE PREVENCIÓN Y CONTROL', bold: true, size: 40, color: COLOR_BREEAM, font: 'Calvert MT' })],
      alignment: AlignmentType.CENTER, spacing: { before: 1200, after: 60 },
    }),
    new Paragraph({
      children: [new TextRun({ text: 'DE LA CALIDAD DEL AIRE INTERIOR (PPCAI)', bold: true, size: 40, color: COLOR_BREEAM, font: 'Calvert MT' })],
      alignment: AlignmentType.CENTER, spacing: { after: 400 },
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Criterio SyB 2 · BREEAM ES Nueva Construcción 2015', italics: true, size: 22, color: '7A6A5A', font: FONT_BODY })],
      alignment: AlignmentType.CENTER, spacing: { after: 800 },
    }),
    p(`Proyecto: ${projectName || '—'}`, { size: 22, bold: true, alignment: AlignmentType.CENTER, after: 100 }),
    p(`Contratista principal: ${contratistaPrincipal?.nombre || '—'}${contratistaPrincipal?.rol ? ` (${contratistaPrincipal.rol})` : ''}`, { size: 20, alignment: AlignmentType.CENTER, after: 100 }),
    p(`Versión ${plan.version} · Estado: ${plan.estado} · Fecha: ${today}`, { size: 18, italics: true, alignment: AlignmentType.CENTER, after: 100, color: '7A6A5A' }),
    new Paragraph({ text: '', pageBreakBefore: true }),
  )

  // ─── Referencia normativa ─────────────────────────────────────────────
  sections.push(
    p('Documento elaborado siguiendo la estructura oficial del Manual Técnico BREEAM ES Nueva Construcción 2015 (IPC-BREEAM-01-12 · Edición 02 · 31/03/2017), criterio SyB 2 · Calidad del aire interior (páginas 70-81 del manual).', { italics: true, color: '7A6A5A', after: 240 }),
  )

  // ═══════════════════════════════════════════════════════════════════════
  //   1 · Datos generales del plan
  // ═══════════════════════════════════════════════════════════════════════
  sections.push(h1('1. Datos generales del plan'))

  const puntosLabel = {
    1: '1 punto · solo el PPCAI implementado',
    2: '2 puntos · PPCAI + cumplimiento Tabla 13 productos',
    3: '3 puntos · PPCAI + Tabla 13 + medición post-construcción',
  }[cj.puntos_objetivo] || `${cj.puntos_objetivo || 1} punto(s)`

  sections.push(
    new Table({
      width: { size: 9360, type: WidthType.DXA },
      columnWidths: [3200, 6160],
      rows: [
        keyValueRow('Objetivo del plan', cj.objetivo),
        keyValueRow('Ámbito de aplicación', cj.ambito),
        keyValueRow('Cronograma de implementación', cj.cronograma),
        keyValueRow('Puntos del criterio pretendidos', puntosLabel),
        keyValueRow('Contratista responsable', contratistaPrincipal?.nombre || '—'),
      ],
    }),
    p('', { after: 200 }),
  )

  // ═══════════════════════════════════════════════════════════════════════
  //   2 · Los cinco elementos oficiales del PPCAI (a-e)
  // ═══════════════════════════════════════════════════════════════════════
  sections.push(h1('2. Los cinco elementos oficiales del PPCAI'))
  sections.push(
    p('Para obtener el primer punto del criterio SyB 2, el pliego oficial del Manual BREEAM ES NC 2015 (criterio 1) exige que el PPCAI aborde los cinco elementos siguientes (a-e). Se recogen las medidas que el contratista se compromete a aplicar en obra.', { italics: true, color: '7A6A5A', after: 200 }),
  )

  ;(cj.elementos_manual || []).forEach((el, i) => {
    const elDef = ELEMENTOS_MANUAL[i] || {}
    sections.push(h2(`2.${el.codigo}. ${elDef.titulo || el.titulo}`))
    if (elDef.referencia_manual) {
      sections.push(p(elDef.referencia_manual, { italics: true, color: '7A6A5A', after: 120, size: 16 }))
    }

    const seleccionadas = el.medidas_seleccionadas || []
    if (seleccionadas.length > 0) {
      sections.push(subhead('Medidas comprometidas'))
      seleccionadas.forEach(idx => {
        const med = (elDef.medidas || [])[idx]
        if (med) sections.push(bullet(med))
      })
    } else {
      sections.push(p('Sin medidas comprometidas para este elemento.', { italics: true, color: '7A6A5A' }))
    }

    if (el.medidas_adicionales && el.medidas_adicionales.trim()) {
      sections.push(subhead('Medidas adicionales específicas del proyecto'))
      el.medidas_adicionales.split('\n').filter(x => x.trim()).forEach(linea => sections.push(bullet(linea.trim())))
    }

    if (el.responsable || el.frecuencia_control) {
      sections.push(subhead('Responsable y control'))
      sections.push(
        new Table({
          width: { size: 9360, type: WidthType.DXA },
          columnWidths: [3200, 6160],
          rows: [
            keyValueRow('Responsable de la aplicación', el.responsable),
            keyValueRow('Frecuencia de control', FRECUENCIAS[el.frecuencia_control] || el.frecuencia_control || '—'),
          ],
        }),
        p('', { after: 200 }),
      )
    }
  })

  // ═══════════════════════════════════════════════════════════════════════
  //   3 · Criterios adicionales del punto 1
  // ═══════════════════════════════════════════════════════════════════════
  const dh = ca.distancias_hvac || {}
  const dv = ca.diseno_ventilacion || {}
  const sc = ca.sensores_co2 || {}
  const hayCriteriosAdicionales = dh.aplica || dv.cumple_une13779 || sc.aplica

  if (hayCriteriosAdicionales) {
    sections.push(h1('3. Criterios adicionales del punto 1 del manual'))

    if (dh.aplica) {
      sections.push(h2('3.1. Distancias mínimas en el sistema HVAC'))
      sections.push(
        new Table({
          width: { size: 9360, type: WidthType.DXA },
          columnWidths: [3200, 6160],
          rows: [
            keyValueRow('Aplica al proyecto', 'Sí · edificio climatizado'),
            keyValueRow('Distancia > 10 m entre tomas y salidas de aire', dh.tomas_salidas_10m ? 'Sí' : 'No'),
            keyValueRow('Distancia > 20 m entre tomas y fuentes contaminación exterior', dh.tomas_fuentes_20m ? 'Sí' : 'No'),
            keyValueRow('Justificación', dh.justificacion),
          ],
        }),
        p('', { after: 200 }),
      )
    }

    if (dv.cumple_une13779) {
      sections.push(h2('3.2. Diseño de ventilación conforme a UNE-EN 13779:2008'))
      sections.push(
        new Table({
          width: { size: 9360, type: WidthType.DXA },
          columnWidths: [3200, 6160],
          rows: [
            keyValueRow('Cumple UNE-EN 13779:2008', 'Sí'),
            keyValueRow('Caudales de aire exterior verificados', dv.caudales_verificados),
            keyValueRow('Referencia del cálculo', dv.referencia_calculo),
          ],
        }),
        p('', { after: 200 }),
      )
    }

    if (sc.aplica) {
      sections.push(h2('3.3. Sensores de CO₂ en zonas de ocupación elevada e impredecible'))
      sections.push(
        new Table({
          width: { size: 9360, type: WidthType.DXA },
          columnWidths: [3200, 6160],
          rows: [
            keyValueRow('Aplica al proyecto', 'Sí'),
            keyValueRow('Zonas equipadas', (sc.zonas || []).join(', ') || '—'),
            keyValueRow('Conectados al sistema de ventilación', sc.conectado_ventilacion ? 'Sí · regulación automática del caudal' : 'No'),
            keyValueRow('Justificación', sc.justificacion),
          ],
        }),
        p('', { after: 200 }),
      )
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  //   4 · Tabla 13 · Productos por categoría (A-H)
  // ═══════════════════════════════════════════════════════════════════════
  sections.push(h1('4. Tabla 13 · Productos con requisitos COV / formaldehído'))
  sections.push(
    p('Las 8 categorías A-H reproducidas del Manual BREEAM ES NC 2015 (páginas 77-79). Para obtener el segundo punto del criterio SyB 2, todos los productos instalados de las categorías aplicables deben cumplir estos requisitos.', { italics: true, color: '7A6A5A', after: 200 }),
  )

  const aplicables = (cj.productos_por_categoria || []).filter(c => c.aplica)
  const noAplicables = (cj.productos_por_categoria || []).filter(c => !c.aplica)

  aplicables.forEach(cat => {
    sections.push(h2(`Categoría ${cat.codigo} · ${cat.label}`))

    const rows = []
    if (cat.criterio_comportamiento) {
      rows.push(keyValueRow('Criterio de comportamiento', cat.criterio_comportamiento))
    }
    if (Array.isArray(cat.normas_comportamiento) && cat.normas_comportamiento.length) {
      rows.push(keyValueRow('Normas de comportamiento', cat.normas_comportamiento.join('\n')))
    }
    if (Array.isArray(cat.normas_ensayo) && cat.normas_ensayo.length) {
      rows.push(keyValueRow('Normas de ensayo', cat.normas_ensayo.join('\n')))
    }
    if (cat.confirmacion_fabricante && cat.confirmacion_fabricante !== 'No aplicable') {
      rows.push(keyValueRow('Confirmación adicional del fabricante', cat.confirmacion_fabricante))
    }
    if (cat.notas && cat.notas.trim()) {
      rows.push(keyValueRow('Productos concretos seleccionados en obra', cat.notas))
    }
    sections.push(
      new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [3200, 6160], rows }),
      p('', { after: 200 }),
    )
  })

  if (noAplicables.length) {
    sections.push(subhead('Categorías que no aplican al proyecto'))
    noAplicables.forEach(c => sections.push(bullet(`Categoría ${c.codigo} · ${c.label}`)))
    sections.push(p('', { after: 200 }))
  }

  // ═══════════════════════════════════════════════════════════════════════
  //   5 · Compromiso post-construcción
  // ═══════════════════════════════════════════════════════════════════════
  sections.push(h1('5. Compromiso post-construcción'))
  sections.push(
    p('Objetivos de puntuación del criterio SyB 2 comprometidos por el contratista y el equipo de proyecto:', { after: 120 }),
  )

  const compromisos = [
    { key: 'punto_2_productos', label: 'Punto 2 · Todos los productos instalados de las categorías A-H aplicables cumplen la Tabla 13' },
    { key: 'punto_3a_covt', label: 'Punto 3.a · Medición post-construcción de COVT · umbral < 300 μg/m³ durante 8 h' },
    { key: 'punto_3b_formaldehido', label: 'Punto 3.b · Medición post-construcción de formaldehído · umbral ≤ 100 μg/m³ durante 30 min (OMS)' },
    { key: 'ejemplar_formaldehido', label: 'Nivel ejemplar · Formaldehído ≤ 10 μg/m³' },
    { key: 'ejemplar_covt', label: 'Nivel ejemplar · COVT ≤ 60 μg/m³' },
  ]
  compromisos.forEach(c => {
    const marcado = !!pc[c.key]
    sections.push(bullet(`${marcado ? '☑' : '☐'}  ${c.label}`))
  })

  if (Array.isArray(pc.metodos_ensayo) && pc.metodos_ensayo.length) {
    sections.push(subhead('Métodos de ensayo aplicables'))
    pc.metodos_ensayo.forEach(m => sections.push(bullet(m)))
  }

  if (pc.laboratorio) {
    sections.push(subhead('Laboratorio de ensayo'))
    sections.push(p(pc.laboratorio, { after: 120 }))
  }

  if (pc.compromiso_reduccion) {
    sections.push(subhead('Compromiso en caso de superar los umbrales'))
    sections.push(p(pc.compromiso_reduccion, { after: 240 }))
  }

  // ═══════════════════════════════════════════════════════════════════════
  //   6 · Medidas SMACNA (solo si aplica)
  // ═══════════════════════════════════════════════════════════════════════
  if (cj.aplica_smacna) {
    sections.push(h1('6. Medidas de gestión de obra (SMACNA)'))
    sections.push(
      p('Los apartados siguientes son un requisito adicional del pliego del proyecto, complementarios al PPCAI oficial del manual.', { italics: true, color: '7A6A5A', after: 200 }),
    )
    ;(cj.medidas_smacna || []).forEach((ap, i) => {
      const apDef = SMACNA_APARTADOS[i] || {}
      sections.push(h2(`6.${ap.codigo}. ${apDef.titulo || ap.titulo}`))
      const sel = ap.medidas_seleccionadas || []
      if (sel.length > 0) {
        sel.forEach(idx => {
          const med = (apDef.medidas || [])[idx]
          if (med) sections.push(bullet(med))
        })
      } else {
        sections.push(p('Sin medidas comprometidas para este apartado.', { italics: true, color: '7A6A5A' }))
      }
      if (ap.medidas_adicionales && ap.medidas_adicionales.trim()) {
        sections.push(subhead('Medidas adicionales del proyecto'))
        ap.medidas_adicionales.split('\n').filter(x => x.trim()).forEach(linea => sections.push(bullet(linea.trim())))
      }
    })
  }

  // ═══════════════════════════════════════════════════════════════════════
  //   7 · Firmante y notas del asesor
  // ═══════════════════════════════════════════════════════════════════════
  sections.push(h1('7. Firmante técnico del plan'))
  sections.push(
    new Table({
      width: { size: 9360, type: WidthType.DXA },
      columnWidths: [3200, 6160],
      rows: [
        keyValueRow('Nombre y apellidos', plan.firmante_nombre),
        keyValueRow('Cargo', plan.firmante_cargo),
        keyValueRow('Email de contacto', plan.firmante_email),
        keyValueRow('Fecha de firma', plan.firma_fecha),
      ],
    }),
    p('', { after: 300 }),
    p('Firma:', { bold: true, after: 800 }),
    p('_______________________________________________', { after: 400 }),
  )

  if (cj.notas_asesor && cj.notas_asesor.trim()) {
    sections.push(h1('8. Notas del asesor BREEAM'))
    cj.notas_asesor.split('\n').filter(x => x.trim()).forEach(linea => sections.push(p(linea.trim(), { after: 120 })))
  }

  // ═══════════════════════════════════════════════════════════════════════
  //   Documento con estilos oficiales BREEAM
  // ═══════════════════════════════════════════════════════════════════════
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: FONT_BODY, size: 18 },
          paragraph: { spacing: { before: 120, after: 120, line: 276 } },
        },
      },
      paragraphStyles: [
        {
          id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { font: 'Calvert MT', size: 28, bold: true, color: COLOR_BREEAM },
          paragraph: { spacing: { before: 360, after: 160 }, outlineLevel: 0 },
        },
        {
          id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { font: 'Calvert MT', size: 24, bold: true, color: COLOR_BREEAM },
          paragraph: { spacing: { before: 240, after: 100 }, outlineLevel: 1 },
        },
      ],
    },
    sections: [{
      properties: { page: { size: { width: 11906, height: 16838 }, margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
      children: sections,
    }],
  })

  const blob = await Packer.toBlob(doc)
  const proyectoSlug = (projectName || 'proyecto').replace(/\s+/g, '_')
  const fecha = new Date().toISOString().split('T')[0]
  const fileName = `PPCAI_${proyectoSlug}_v${plan.version}_${fecha}.docx`
  saveAs(blob, fileName)
}
